/**
 * ltcRuntime — initializes Three.js's LTC textures for rect area lights.
 *
 * Three.js r182 ships a production-quality LTC implementation via the
 * `RectAreaLightTexturesLib` addon, but the engine must call its `init()`
 * exactly once to upload the two 64×64 LUTs (M⁻¹ matrix and norm/Fresnel
 * coefficients) to the renderer. Without this call the built-in rect
 * area light renders as pure black.
 *
 * We guard with a module-level flag because the LUT data blobs are ~80 KB
 * and decoding them twice is wasteful.
 */

import { RectAreaLightTexturesLib } from 'three/addons/lights/RectAreaLightTexturesLib.js';
import { RectAreaLightNode } from 'three/webgpu';
import { Logger } from '../../../core/Logger.js';

let _initialized = false;

/**
 * Call once during engine startup. Idempotent — subsequent calls are no-ops.
 * Safe to call before the renderer is created.
 */
export function initLTC(): void {
  if (_initialized) return;
  try {
    const ltc = RectAreaLightTexturesLib.init();
    // Three.js r182 exposes both Float and Half variants. RectAreaLightNode
    // picks the appropriate one based on the backend's `float32-filterable`
    // capability — we pass the full lib so the node can decide.
    // setLTC signature: setLTC({ LTC_FLOAT_1, LTC_FLOAT_2, LTC_HALF_1, LTC_HALF_2 })
    (RectAreaLightNode as unknown as { setLTC: (lib: unknown) => void }).setLTC(ltc);
    _initialized = true;
    Logger.info('AreaLight', 'LTC textures initialized (64×64 RGBA16F/32F)');
  } catch (err) {
    Logger.warn('AreaLight',
      `Failed to init LTC textures — rect area lights will render black. Error: ${err}`);
  }
}

/** True if initLTC() has been called successfully. */
export function isLTCInitialized(): boolean {
  return _initialized;
}

/** Test-only. */
export function _resetLTC(): void {
  _initialized = false;
}
